/**
 * Live counters for the Admin sidebar so operators can see at a glance
 * where new work has arrived (recruitment, disputes, members, etc.).
 *
 * All queries are head-only count selects, refreshed every 60s and on
 * window focus. RLS scopes each table to admins only.
 */
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface AdminSidebarCounts {
  recruitmentInbox: number | null;
  disputesOpen: number | null;
  membersUnconverted: number | null;
  waitlist: number | null;
  remoteQuotesNew: number | null;
  variationApprovalsPending: number | null;
}

const ZERO: AdminSidebarCounts = {
  recruitmentInbox: null,
  disputesOpen: null,
  membersUnconverted: null,
  waitlist: null,
  remoteQuotesNew: null,
  variationApprovalsPending: null,
};

async function safeCount(builder: any): Promise<number | null> {
  try {
    const { count, error } = await builder;
    if (error) return null;
    return count;
  } catch {
    return null;
  }
}

export function useAdminSidebarCounts() {
  const [counts, setCounts] = useState<AdminSidebarCounts>(ZERO);

  const refresh = useCallback(async () => {
    const recruitmentBatch = Promise.all([
      safeCount(
        supabase
          .from("recruitment_leads")
          .select("id", { count: "exact", head: true })
          .eq("status", "new")
          .eq("is_test", false)
          .is("deleted_at", null),
      ),
      safeCount(
        supabase
          .from("installer_applications")
          .select("id", { count: "exact", head: true })
          .eq("status", "pending_review")
          .eq("is_test", false)
          .is("deleted_at", null),
      ),
      safeCount(
        supabase
          .from("consultant_applications")
          .select("id", { count: "exact", head: true })
          .eq("status", "new")
          .eq("is_test", false)
          .is("deleted_at", null),
      ),
      safeCount(
        supabase
          .from("product_partner_applications")
          .select("id", { count: "exact", head: true })
          .eq("status", "new")
          .eq("is_test", false)
          .is("deleted_at", null),
      ),
    ]);

    const otherBatch = Promise.all([
      safeCount(
        supabase
          .from("disputes")
          .select("id", { count: "exact", head: true })
          .in("status", ["open", "under_review", "escalated"]),
      ),
      safeCount(
        supabase
          .from("homeowner_members")
          .select("id", { count: "exact", head: true })
          .is("converted_to_customer_id", null)
          .eq("is_test", false),
      ),
      safeCount(
        supabase
          .from("homeowner_waitlist")
          .select("id", { count: "exact", head: true }),
      ),
      safeCount(
        supabase
          .from("remote_quotes")
          .select("id", { count: "exact", head: true })
          .eq("status", "new"),
      ),
      safeCount(
        (supabase as any)
          .from("job_variations")
          .select("id", { count: "exact", head: true })
          .eq("status", "pending"),
      ),
    ]);

    const [rlNew, iaNew, caNew, ppaNew] = await recruitmentBatch;
    const [disputes, members, waitlist, remoteQuotes, variationApprovals] = await otherBatch;

    setCounts({
      recruitmentInbox: [rlNew, iaNew, caNew, ppaNew].some(value => value === null) ? null
        : (rlNew ?? 0) + (iaNew ?? 0) + (caNew ?? 0) + (ppaNew ?? 0),
      disputesOpen: disputes,
      membersUnconverted: members,
      waitlist,
      remoteQuotesNew: remoteQuotes,
      variationApprovalsPending: variationApprovals,
    });
  }, []);

  useEffect(() => {
    void refresh();
    const interval = setInterval(() => void refresh(), 60_000);
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  return counts;
}
